import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/authStorage';
import './HistoricalDataUpload.css';

interface Project {
  id: number;
  name: string;
}

interface UploadResult {
  totalProcessed: number;
  created: number;
  skipped: number;
  errors: string[];
  bookingsCreated: string[];
}

interface PreviewTransaction {
  date: string;
  unitNo: string;
  amount: number;
  cr?: number;
  type: string;
  mode?: string;
  refNumber?: string;
}

interface PreviewBooking {
  customerName: string;
  unitNo: string;
  wing?: string;
  unitType: string;
  bookingType: string;
  transferTo?: string;
  saleDeed: {
    incomeTransactions: PreviewTransaction[];
    refundTransactions: PreviewTransaction[];
    totalIncome: number;
    totalRefunds: number;
    netAmount: number;
  };
  hidden: {
    incomeTransactions: PreviewTransaction[];
    refundTransactions: PreviewTransaction[];
    totalIncome: number;
    totalRefunds: number;
    netAmount: number;
  };
}

interface PreviewData {
  projectName: string;
  totalBookings: number;
  previewData: PreviewBooking[];
}

const HistoricalDataUpload: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'result'>('upload');

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data || []);
      } else {
        setError('Failed to load projects');
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Error loading projects');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a valid Excel file (.xlsx or .xls)');
        setSelectedFile(null);
      }
    }
  };

  const handlePreview = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    if (!selectedFile) {
      setError('Please select an Excel file');
      return;
    }

    setPreviewing(true);
    setError('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('projectId', selectedProject);

      const response = await fetch(`${API_BASE_URL}/historical-data/preview-historical-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setPreviewData(result.data);
        setCurrentStep('preview');
      } else {
        setError(result.message || 'Failed to preview data');
      }
    } catch (error) {
      console.error('Error previewing data:', error);
      setError('Error previewing data');
    } finally {
      setPreviewing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedProject || !selectedFile) {
      setError('Missing project or file selection');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('projectId', selectedProject);

      const response = await fetch(`${API_BASE_URL}/historical-data/upload-historical-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResult(result.data);
        setCurrentStep('result');
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setError(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleBackToUpload = () => {
    setCurrentStep('upload');
    setPreviewData(null);
    setUploadResult(null);
    setError('');
  };

  const handleProceedWithUpload = () => {
    handleUpload();
  };

  const downloadTemplate = () => {
    // Create a simple template file structure information
    const info = `
Historical Data Upload Template

Required Excel file with 3 sheets:

Sheet 1: "Data"
Columns: Unit Type | Unit No | Type | TransferTo | Name
Example: Shop | 201 | Cancel | | Rupeshbhai Natwarbhai Prajapti

Sheet 2: "Hidden" 
Columns: Date | Unit No | Cr | Type
Example: 07/12/2022 | 602-Cancel | 50.00 | income

Sheet 3: "SaleDeed"
Columns: Date | Unit No | Name | Amount | Mode | Ref Number | Chq No | Bank Name | Type
Example: 17/11/2025 | B-104 | Urmilaben Rameshkumar Joshi | 94900 | MB | 998579448381 | | | Income

Important Notes:
- Date format: DD/MM/YYYY
- Unit numbers can have wings (B-104) or be simple (201)
- "-Cancel", "-Cancel-1" suffixes will be automatically removed
- Type can be "Cancel" or "Transfer" in Data sheet
- Type can be "income" or "Return" in Hidden/SaleDeed sheets
`;

    const blob = new Blob([info], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'historical_data_template_info.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="historical-upload-container">
      <div className="upload-header">
        <h2>📊 Historical Data Upload</h2>
        <p>Upload historical cancelled booking data with financial transactions</p>
        
        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${currentStep === 'upload' ? 'active' : currentStep === 'preview' || currentStep === 'result' ? 'completed' : ''}`}>
            1. Upload File
          </div>
          <div className={`step ${currentStep === 'preview' ? 'active' : currentStep === 'result' ? 'completed' : ''}`}>
            2. Preview Data
          </div>
          <div className={`step ${currentStep === 'result' ? 'active' : ''}`}>
            3. Results
          </div>
        </div>
      </div>

      {/* Step 1: Upload Form */}
      {currentStep === 'upload' && (
        <div className="upload-form">
          {/* Project Selection */}
          <div className="form-group">
            <label htmlFor="project-select">📁 Select Project *</label>
            <select
              id="project-select"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              disabled={previewing}
            >
              <option value="">-- Select Project --</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div className="form-group">
            <label htmlFor="file-input">📄 Excel File (with Data, Hidden, SaleDeed sheets) *</label>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={previewing}
            />
            {selectedFile && (
              <div className="file-info">
                ✅ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Template Download */}
          <div className="form-group">
            <button 
              type="button" 
              onClick={downloadTemplate}
              className="template-btn"
              disabled={previewing}
            >
              📋 Download Template Info
            </button>
          </div>

          {/* Preview Button */}
          <div className="form-group">
            <button
              onClick={handlePreview}
              disabled={previewing || !selectedProject || !selectedFile}
              className="preview-btn"
            >
              {previewing ? '⏳ Analyzing Data...' : '� Preview Data'}
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          {/* Instructions */}
          <div className="instructions">
            <h3>📝 Instructions:</h3>
            <ol>
              <li>Prepare Excel file with exactly 3 sheets named: <strong>"Data"</strong>, <strong>"Hidden"</strong>, <strong>"SaleDeed"</strong></li>
              <li><strong>Data sheet:</strong> Contains booking information (Unit Type, Unit No, Type, Name, etc.)</li>
              <li><strong>Hidden sheet:</strong> Contains hidden/receivable income transactions (encrypted in systems DB)</li>
              <li><strong>SaleDeed sheet:</strong> Contains regular sale deed income transactions (stored in main DB)</li>
              <li>Select the target project and upload file to preview the data first</li>
            </ol>
          </div>
        </div>
      )}

      {/* Step 2: Preview Data */}
      {currentStep === 'preview' && previewData && (
        <div className="preview-container">
          <div className="preview-header">
            <h3>🔍 Data Preview - {previewData.projectName}</h3>
            <p>Found <strong>{previewData.totalBookings}</strong> unique customer+unit combinations. Please review before proceeding:</p>
          </div>

          <div className="preview-data">
            {previewData.previewData.map((booking, index) => (
              <div key={index} className="booking-preview">
                <div className="booking-header">
                  <h4>
                    {booking.customerName} - {booking.wing || ''}{booking.unitNo} 
                    <span className={`booking-type ${booking.bookingType}`}>
                      {booking.bookingType.toUpperCase()}
                      {booking.transferTo && ` → ${booking.transferTo}`}
                    </span>
                  </h4>
                </div>

                <div className="transaction-summary">
                  {/* Sale Deed Summary */}
                  <div className="transaction-group sale-deed">
                    <h5>💰 Sale Deed Transactions (Main)</h5>
                    <div className="summary-stats">
                      <span className="income">Income: ₹{booking.saleDeed.totalIncome.toLocaleString()}</span>
                      <span className="refund">Refunds: ₹{booking.saleDeed.totalRefunds.toLocaleString()}</span>
                      <span className="net">Net: ₹{booking.saleDeed.netAmount.toLocaleString()}</span>
                    </div>
                    
                    {booking.saleDeed.incomeTransactions.length > 0 && (
                      <details className="transaction-details">
                        <summary>Income Transactions ({booking.saleDeed.incomeTransactions.length})</summary>
                        {booking.saleDeed.incomeTransactions.map((txn, i) => (
                          <div key={i} className="transaction">
                            {txn.date} - ₹{txn.amount.toLocaleString()} ({txn.mode || 'Receivable'})
                          </div>
                        ))}
                      </details>
                    )}
                    
                    {booking.saleDeed.refundTransactions.length > 0 && (
                      <details className="transaction-details">
                        <summary>Refund Transactions ({booking.saleDeed.refundTransactions.length})</summary>
                        {booking.saleDeed.refundTransactions.map((txn, i) => (
                          <div key={i} className="transaction refund">
                            {txn.date} - ₹{txn.amount.toLocaleString()} ({txn.mode || 'Receivable'})
                          </div>
                        ))}
                      </details>
                    )}
                  </div>

                  {/* Hidden Summary */}
                  <div className="transaction-group hidden">
                    <h5>🔐 Hidden Transactions (Receivables - Encrypted)</h5>
                    <div className="summary-stats">
                      <span className="income">Income: ₹{booking.hidden.totalIncome.toLocaleString()}</span>
                      <span className="refund">Refunds: ₹{booking.hidden.totalRefunds.toLocaleString()}</span>
                      <span className="net">Net: ₹{booking.hidden.netAmount.toLocaleString()}</span>
                    </div>
                    
                    {booking.hidden.incomeTransactions.length > 0 && (
                      <details className="transaction-details">
                        <summary>Income Transactions ({booking.hidden.incomeTransactions.length})</summary>
                        {booking.hidden.incomeTransactions.map((txn, i) => (
                          <div key={i} className="transaction">
                            {txn.date} - ₹{(txn.cr || 0).toLocaleString()}
                          </div>
                        ))}
                      </details>
                    )}
                    
                    {booking.hidden.refundTransactions.length > 0 && (
                      <details className="transaction-details">
                        <summary>Refund Transactions ({booking.hidden.refundTransactions.length})</summary>
                        {booking.hidden.refundTransactions.map((txn, i) => (
                          <div key={i} className="transaction refund">
                            {txn.date} - ₹{(txn.cr || 0).toLocaleString()}
                          </div>
                        ))}
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="preview-actions">
            <button onClick={handleBackToUpload} className="back-btn">
              ← Back to Upload
            </button>
            <button 
              onClick={handleProceedWithUpload} 
              className="proceed-btn"
              disabled={uploading}
            >
              {uploading ? '⏳ Creating Bookings...' : '✅ Proceed with Upload'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Results */}
      {currentStep === 'result' && uploadResult && (
        <div className="results-container">
          <div className="success-result">
            <h3>✅ Upload Completed Successfully!</h3>
            <div className="result-stats">
              <div className="stat">
                <strong>📊 Total Processed:</strong> {uploadResult.totalProcessed}
              </div>
              <div className="stat">
                <strong>✅ Successfully Created:</strong> {uploadResult.created}
              </div>
              <div className="stat">
                <strong>⚠️ Skipped:</strong> {uploadResult.skipped}
              </div>
              <div className="stat">
                <strong>❌ Errors:</strong> {uploadResult.errors.length}
              </div>
            </div>

            {uploadResult.bookingsCreated.length > 0 && (
              <div className="created-bookings">
                <h4>📋 Created Bookings:</h4>
                <div className="booking-list">
                  {uploadResult.bookingsCreated.slice(0, 10).map((booking, index) => (
                    <div key={index} className="booking-item">
                      {booking}
                    </div>
                  ))}
                  {uploadResult.bookingsCreated.length > 10 && (
                    <div className="more-items">
                      ... and {uploadResult.bookingsCreated.length - 10} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {uploadResult.errors.length > 0 && (
              <div className="error-list">
                <h4>❌ Errors:</h4>
                <div className="errors">
                  {uploadResult.errors.slice(0, 5).map((error, index) => (
                    <div key={index} className="error-item">
                      {error}
                    </div>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <div className="more-items">
                      ... and {uploadResult.errors.length - 5} more errors
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="result-actions">
              <button onClick={handleBackToUpload} className="new-upload-btn">
                📊 Upload Another File
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalDataUpload;